import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AppIconComponent } from '../../../../../../../common/components/icon/icon.component';
import { FormInputDirective } from '../../../../../../../common/directives/form-input.directive';
import { InsuranceTypeCoverageDto } from '../../../../../../../core/data/dtos/insurance-type-coverage.dto';
import { CoverageAmountInputComponent } from '../../../../../../../common/components/coverage-amount-input/coverage-amount-input.component';
import { formatDescription } from '../../../../../../../common/utils/format-description';

@Component({
  selector: 'app-coverage-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppIconComponent,
    FormInputDirective,
    CoverageAmountInputComponent,
  ],
  templateUrl: './coverage-selector.component.html',
})
export class CoverageSelectorComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  @Input() primaryCoverage?: InsuranceTypeCoverageDto;
  @Input() additionalCoverages: InsuranceTypeCoverageDto[] = [];
  @Input() calculatedCoverages: InsuranceTypeCoverageDto[] = [];
  @Input() isEnfermedadesGraves: boolean = false;
  @Input() hasNoPrimaryCoverage: boolean = false;
  @Input() coverageError: string | null = null;
  @Input() insuredCount: number = 1;
  /** Age-band maximum for the primary coverage (null when no band applies). */
  @Input() ageAmountCap: number | null = null;
  @Input() getCoverageRange!: (
    cov?: InsuranceTypeCoverageDto,
  ) => { min?: number; max?: number; minAmount?: number; maxAmount?: number } | null;

  uniqueId = Math.random().toString(36).substring(2, 9);

  /**
   * Returns the effective max for an additional coverage, capped by the main
   * coverage value when a primary coverage exists.
   */
  getEffectiveMax(cov: InsuranceTypeCoverageDto): number | undefined {
    const rangeMax = this.getCoverageRange(cov)?.maxAmount;
    const mainValue = Number(this.form.get('mainCoverage')?.value ?? 0);
    const hasPrimary = !this.isEnfermedadesGraves && !this.hasNoPrimaryCoverage && mainValue > 0;
    if (!hasPrimary) return rangeMax;
    if (rangeMax != null) return Math.min(rangeMax, mainValue);
    return mainValue;
  }

  isFixedLimit(cov?: InsuranceTypeCoverageDto): boolean {
    if (!cov || !cov.currencies) return false;
    const currentCurrency = this.form.get('currency')?.value ?? 'RD$';
    const currencyDto = cov.currencies.find(c => c.currency === currentCurrency);
    return currencyDto?.isFixedLimit ?? false;
  }

  /** Returns true when the coverage has an insurePrimaryFraction > 0. */
  hasFraction(cov?: InsuranceTypeCoverageDto): boolean {
    return !!cov && typeof cov.insurePrimaryFraction === 'number' && cov.insurePrimaryFraction > 0;
  }

  @ViewChildren('infoButton', { read: ElementRef }) infoButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  private popoverCtor?: typeof import('bootstrap/js/dist/popover.js').default;
  private readonly popovers = new Map<
    HTMLElement,
    import('bootstrap/js/dist/popover.js').default
  >();

  selectedKey(cov: InsuranceTypeCoverageDto): string {
    return `selected_${cov.id ?? cov.name ?? ''}`;
  }

  amountKey(cov: InsuranceTypeCoverageDto): string {
    return `amount_${cov.id ?? cov.name ?? ''}`;
  }

  onInfoClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement | null;
    button?.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node | null;
    if (!target) return;

    const clickedTrigger = Array.from(this.popovers.keys()).some((el) =>
      el.contains(target),
    );
    const clickedPopover = (target as HTMLElement).closest('.popover');
    if (clickedTrigger || clickedPopover) return;

    this.popovers.forEach((popover) => popover.hide());
  }

  async ngAfterViewInit(): Promise<void> {
    await this.ensurePopovers();
    this.infoButtons?.changes?.subscribe(() => {
      void this.ensurePopovers();
    });
  }

  ngOnDestroy(): void {
    this.detachPopovers();
  }

  private async ensurePopovers(): Promise<void> {
    if (typeof window === 'undefined') return;

    const buttons = this.infoButtons ?? [];
    const elements = new Set<HTMLElement>();
    buttons.forEach((b: ElementRef<HTMLButtonElement>) =>
      elements.add(b.nativeElement),
    );

    if (!elements.size) {
      this.detachPopovers();
      return;
    }

    if (!this.popoverCtor) {
      const { default: Popover } = await import('bootstrap/js/dist/popover.js');
      this.popoverCtor = Popover;
    }

    this.popovers.forEach((popover, element) => {
      if (!elements.has(element)) {
        popover.dispose();
        this.popovers.delete(element);
      }
    });

    elements.forEach((element) => {
      if (this.popovers.has(element) || !this.popoverCtor) return;

      const title = element.dataset['infoTitle'] ?? '';
      const content = formatDescription(element.dataset['infoContent'] ?? '');
      const popover = new this.popoverCtor(element, {
        trigger: 'click',
        title,
        content,
        container: 'body',
        html: true,
        customClass: 'custom-popover',
      });
      this.popovers.set(element, popover);
    });
  }

  private detachPopovers(): void {
    this.popovers.forEach((popover) => popover.dispose());
    this.popovers.clear();
  }
}
